import { state } from 'cerebral'
import {
  complement,
  compose,
  filter,
  find,
  flatten,
  fromPairs,
  groupBy,
  ifElse,
  indexBy,
  isNil,
  map,
  mapObjIndexed,
  path,
  pathOr,
  pluck,
  prop,
  propEq,
  sortBy,
  toPairs,
} from 'ramda'
import { capitalize } from 'underscore.string'
import { mapEventLabel } from '../../lib/util/event-label'

const eventLabelsByType = compose(groupBy(prop('category')), map(mapEventLabel))
const mapAppointmentTypes = compose(map(mapEventLabel))

export const packages = (get) => {
  const organization = get(state`account.organization`)
  const location = get(state`account.location`)
  const packages = location?.packages || organization?.packages || []
  return fromPairs(packages.map(({ slug }) => [slug, true]))
}

export const readOnlyEvents = (get) => {
  const packages = get(state`account.packages`)
  return Boolean(packages.rsvps && !packages.events)
}

export const readOnlyPeople = (get) => {
  const packages = get(state`account.packages`)
  return Boolean(packages.rsvps && !packages.people)
}

export const availableScreens = (get) => {
  const organization = get(state`account.organization`)
  const location = get(state`account.location`)
  const packages = location?.packages || organization?.packages || []
  const { screenLabels = {} } = organization || {}
  const setNameByTemplate = (screen) => ({ ...screen, name: screenLabels[screen.template]?.trim() || screen.name })
  const screens = sortBy(prop('name'), flatten(filter(Boolean, pluck('enablesScreens', packages))).map(setNameByTemplate))
  return indexBy(prop('_id'), screens)
}

export const organization = (get) => {
  const orgSlug = get(state`account.orgSlug`)
  const resorts = get(state`account.currentUser.resorts`) || []
  const resort = find(propEq(orgSlug, 'slug'), resorts) || resorts[0]
  return resort
}

const getComputedOrg = (get) => get(state`account.organization`)
export const labels = compose(prop('mobileLabel'), getComputedOrg)
export const sections = compose(prop('mobileSections'), getComputedOrg)
export const currentProfile = compose(prop('mobileUserProfile'), getComputedOrg)
export const appointmentLabels = compose(mapAppointmentTypes, pathOr([], ['appointmentSettings', 'labels']), getComputedOrg)
export const appointmentLabelsMap = (get) => {
  const appointmentsByLabel = get(state`account.appointmentLabels`) || []
  return indexBy(prop('label'), appointmentsByLabel)
}
export const eventLocationCode = compose(path(['eventSettings', 'locationCode']), getComputedOrg)
export const eventLabels = compose(eventLabelsByType, pathOr([], ['eventSettings', 'eventLabels']), getComputedOrg)
export const eventLabelsMap = (get) => {
  const labelsByType = get(state`account.eventLabels`)
  return mapObjIndexed(indexBy(prop('_id')), labelsByType)
}

export const orgRoles = compose(prop('mobileRoles'), getComputedOrg)
export const orgRolesOptions = compose(
  sortBy(prop('value')),
  map(([key]) => ({ value: key, label: capitalize(key) })),
  filter(prop(1)),
  toPairs,
  orgRoles
)

// TODO: It seems this should use generateStats from alpine-common. The logic is duplicated.
const liftIsOpen = ifElse(prop('statusOverride'), propEq('open', 'statusOverride'), propEq('status', 'open'))
const trailShouldIncludeInCount = complement(propEq(false, 'includeInTrailCount'))
const trailIsOpen = ifElse(compose(isNil, path(['override', 'open'])), prop('open'), path(['override', 'open']))

const percent = (numerator, denominator) => (denominator ? Math.floor((numerator / denominator) * 100) : 0)

export const season = (get) => {
  const org = getComputedOrg(get)
  if (!org) {
    return
  }

  const values = get(state`account.organization.scrapedData.stats`) || {}
  const overrides = get(state`account.organization.overrideData.stats`) || {}
  const seasons = get(state`account.currentUser.seasons`)
  const season = find(propEq(org._id, 'resort'), seasons)
  const lifts = get(state`lifts.list.data`)
  const trails = get(state`trails.list.data`).filter(trailShouldIncludeInCount)

  // Populate stats for selected season. Used by snow stats.
  // TODO: It seems this should use generateStats from alpine-common. The logic is duplicated.
  if (season) {
    const lists = ['stats', 'secondaryStats', 'featureStats', 'hiddenStats']
    lists.forEach((listName) => {
      const list = season[listName] || []
      list.forEach((stat) => {
        const { slug } = stat
        if (stat.path?.length > 0) {
          const statPath = path(stat.path)
          const value = statPath(values)
          const override = statPath(overrides)
          if (!isNil(value)) {
            stat.value = value
          }
          if (!isNil(override)) {
            stat.override = override
          }
        } else if (slug === 'lifts-percent' || slug === 'lifts-count' || slug === 'lifts-total') {
          if (lifts.length === 0 || slug === 'lifts-total') {
            stat.value = lifts.length
          } else {
            const openCount = lifts.filter(liftIsOpen).length
            stat.value = slug === 'lifts-count' ? openCount : percent(openCount, lifts.length)
          }
        } else if (slug === 'trails-percent' || slug === 'trails-count' || slug === 'trails-total') {
          if (trails.length === 0 || slug === 'trails-total') {
            stat.value = trails.length
          } else {
            const openCount = trails.filter(trailIsOpen).length
            stat.value = slug === 'trails-count' ? openCount : percent(openCount, trails.length)
          }
        }
      })
    })
    // Special case for conditions.
    // TODO: Make a consistent pattern for this.
    season.conditionsOverride = overrides.conditions
  }

  return season
}
